import { isEmpty, isNotEmpty, sanitizeValues } from '@intelligence/v3/utils/common';
import {
  getDefaultApiFieldMap,
  getDefaultUrlFieldMap,
  getItemApiParamsFieldMap,
  getItemUrlParamsFieldMap,
} from '../shared/fieldMapUtils';
import { getStringArrayApiValueByItem } from '../shared/valueUtils';

import type { IFilterMiddlewareUtils } from '../shared/types';
import type { IFilterItemConfig } from '@intelligence/v3/filterCore/types';
import type { IGameEngineFilterValue } from './types';

const VALID_LOGIC: IRelation[] = ['AND', 'OR'];
const LOGIC_FIELD = 'game_engine_relation';
const VALUE_FIELD = 'game_engine';
const URL_LOGIC_FIELD = 'gameEngineRelation';
const URL_VALUE_FIELD = 'gameEngine';

const getApiFieldMap = (
  item: IFilterItemConfig,
  params?: unknown,
): {
  logicField: string;
  valueField: string;
} => {
  const apiParamsFieldMap = getItemApiParamsFieldMap(item, params);
  const defaultApiFieldMap = getDefaultApiFieldMap(params);

  return {
    logicField:
      apiParamsFieldMap?.logic ||
      apiParamsFieldMap?.relation ||
      defaultApiFieldMap.logic ||
      defaultApiFieldMap.relation ||
      LOGIC_FIELD,
    valueField: apiParamsFieldMap?.value || defaultApiFieldMap.value || VALUE_FIELD,
  };
};

const getUrlFieldMap = (
  item: IFilterItemConfig,
  params?: unknown,
): {
  logicField: string;
  valueField: string;
} => {
  const urlParamsFieldMap = getItemUrlParamsFieldMap(item, params);
  const defaultUrlFieldMap = getDefaultUrlFieldMap(params);

  return {
    logicField:
      urlParamsFieldMap?.logic ||
      urlParamsFieldMap?.relation ||
      defaultUrlFieldMap.logic ||
      defaultUrlFieldMap.relation ||
      URL_LOGIC_FIELD,
    valueField: urlParamsFieldMap?.value || defaultUrlFieldMap.value || URL_VALUE_FIELD,
  };
};

/**
 * 校验并规范化 logic 值，无效时回退为 'OR'
 */
const normalizeLogic = (logic?: IRelation): IRelation => {
  if (logic && VALID_LOGIC.includes(logic)) {
    return logic;
  }
  return 'OR';
};

const serializeLogic = (logic?: IRelation): 'and' | 'or' => {
  return normalizeLogic(logic) === 'AND' ? 'and' : 'or';
};

/**
 * 游戏引擎筛选器中间件工具
 * value 类型：IRelationalValue { logic?: IRelation; value: string[] }
 *
 * 校验规则：
 * - value 必须为非空字符串数组，自动去重
 * - logic 必须为 'AND' | 'OR'，无效时回退为 'OR'
 */
class GameEngineMiddlewareUtils implements IFilterMiddlewareUtils<IGameEngineFilterValue, IRecord<any>> {
  convertToApiParams(
    item: IFilterItemConfig,
    filterValue?: IGameEngineFilterValue,
    params?: unknown,
  ): IRecord<any> | null {
    const values = filterValue?.value;

    if (isEmpty(values)) {
      return null;
    }

    const { value: unique, isAllSelected } = getStringArrayApiValueByItem(item, values);

    if (isEmpty(unique) && !isAllSelected) {
      return null;
    }

    const { logicField, valueField } = getApiFieldMap(item, params);

    return {
      [logicField]: serializeLogic(filterValue?.logic),
      [valueField]: unique,
    };
  }

  getManagedUrlKeys(item: IFilterItemConfig, params?: unknown): string[] {
    const { logicField, valueField } = getUrlFieldMap(item, params);

    return [logicField, valueField].filter(isNotEmpty);
  }

  serializeUrlParams(
    item: IFilterItemConfig,
    filterValue?: IGameEngineFilterValue,
    params?: unknown,
  ): IRecord<string> | null {
    const values = filterValue?.value;

    if (isEmpty(values)) {
      return null;
    }

    const unique = sanitizeValues(values);

    if (isEmpty(unique)) {
      return null;
    }

    const { logicField, valueField } = getUrlFieldMap(item, params);

    return {
      [logicField]: serializeLogic(filterValue?.logic),
      [valueField]: unique.join(','),
    };
  }

  deserializeUrlParams(
    item: IFilterItemConfig,
    searchParams: URLSearchParams,
    params?: unknown,
  ): IGameEngineFilterValue | null {
    if (isEmpty(searchParams)) {
      return null;
    }

    const { logicField, valueField } = getUrlFieldMap(item, params);
    const logic = normalizeLogic((searchParams.get(logicField) || '').toUpperCase() as IRelation);
    const unique = sanitizeValues((searchParams.get(valueField) || '').split(',').map((itemValue) => itemValue.trim()));

    if (isEmpty(unique)) {
      return null;
    }

    return { logic, value: unique };
  }
}

export default GameEngineMiddlewareUtils;
